import { allOrganizationsHaveRelationalProducts } from '@/helpers/relationalProducts';
import { stinaProductIdFromGroupKey } from '@/helpers/staticTicket';
import { PRODUCT_CLASS_PREFIX } from '@/helpers/ticketClass';
import type {
  BobcatOfferGroupDTO,
  BobcatTagDTO,
  OrganizationAllowedBobcatOfferGroupDTO,
  OrganizationAllowedBobcatTagDTO,
} from '@/api/trafikportalen';
import type { CollectionTypeDTO } from '@/api/trafikportalen/models/collectionTypeDTO';

/**
 * The organisation's allowed offer groups for the category with the period's
 * product added, or null when the product is already allowed.
 */
export const offerGroupIdsAllowingProduct = (
  allowed: OrganizationAllowedBobcatOfferGroupDTO[],
  offerGroups: BobcatOfferGroupDTO[],
  collectionType: CollectionTypeDTO,
  stinaProductId: string,
): string[] | null => {
  const allowedIds = allowed
    .filter((group) => group.collection_type === collectionType)
    .map((group) => group.id);
  const missingIds = offerGroups
    .filter(
      (group) =>
        group.sales_channel === 'school' &&
        stinaProductIdFromGroupKey(group.group_key) === stinaProductId &&
        !allowedIds.includes(group.id),
    )
    .map((group) => group.id);
  if (missingIds.length === 0) return null;
  return [...allowedIds, ...missingIds];
};

export interface PeriodTagChanges {
  /** Allowed for every selected organisation. */
  tagIds: string[];
  /** Removed only from the organisations in `removableOrganizationIds`. */
  removedTagIds: string[];
  removableOrganizationIds: string[];
}

export const NO_PERIOD_TAG_CHANGES: PeriodTagChanges = {
  tagIds: [],
  removedTagIds: [],
  removableOrganizationIds: [],
};

/**
 * The organisation's allowed tags for the category with the period's tags
 * added and the removed ones dropped, or null when nothing changes.
 */
export const tagIdsAfterChanges = (
  allowed: OrganizationAllowedBobcatTagDTO[],
  collectionType: CollectionTypeDTO,
  tagIds: string[],
  removedTagIds: string[],
): string[] | null => {
  const allowedIds = allowed
    .filter((tag) => tag.collection_type === collectionType)
    .map((tag) => tag.id);
  const keptIds = allowedIds.filter((id) => !removedTagIds.includes(id));
  const missingIds = tagIds.filter((id) => !allowedIds.includes(id));
  if (keptIds.length === allowedIds.length && missingIds.length === 0) {
    return null;
  }
  return [...keptIds, ...missingIds];
};

interface OrganizationAllowedTags {
  selfManaged: boolean;
  allowed: OrganizationAllowedBobcatTagDTO[];
}

export interface RelationalTagChoices {
  /** Ticket categories that can be chosen for the period. */
  available: BobcatTagDTO[];
  /** Available ticket categories some organisation already has. */
  activeIds: string[];
  everyOrganizationHasTags: boolean;
}

/**
 * Organisations that manage their own periods keep their own ticket
 * categories, so only the categories they all have can be chosen. Without
 * them, every ticket category can be chosen. Categories an organisation
 * already has start out chosen.
 */
export const relationalTagChoices = (
  tags: BobcatTagDTO[],
  organizations: OrganizationAllowedTags[],
  collectionType: CollectionTypeDTO,
): RelationalTagChoices => {
  const productClasses = tags.filter((tag) =>
    tag.tag_id.startsWith(PRODUCT_CLASS_PREFIX),
  );
  const idsByOrganization = organizations.map(({ selfManaged, allowed }) => ({
    selfManaged,
    ids: allowedTagIds(allowed, collectionType),
  }));
  const selfManaged = idsByOrganization.filter((org) => org.selfManaged);
  const available = productClasses.filter((tag) =>
    selfManaged.every(({ ids }) => ids.has(tag.id)),
  );

  return {
    available,
    activeIds: available
      .filter((tag) => idsByOrganization.some(({ ids }) => ids.has(tag.id)))
      .map((tag) => tag.id),
    everyOrganizationHasTags: allOrganizationsHaveRelationalProducts(
      organizations.map(({ allowed }) => allowed),
      collectionType,
    ),
  };
};

/**
 * Whether a removed ticket category has to stay for some organisation, since
 * categories cannot be removed when a period was active this or last month.
 */
export const isTagRemovalBlocked = (
  organizations: (OrganizationAllowedTags & { recentPeriod: boolean })[],
  collectionType: CollectionTypeDTO,
  removedTagIds: string[],
): boolean =>
  organizations.some(
    ({ selfManaged, recentPeriod, allowed }) =>
      !selfManaged &&
      recentPeriod &&
      removedTagIds.some((id) =>
        allowedTagIds(allowed, collectionType).has(id),
      ),
  );

const allowedTagIds = (
  allowed: OrganizationAllowedBobcatTagDTO[],
  collectionType: CollectionTypeDTO,
) =>
  new Set(
    allowed
      .filter((tag) => tag.collection_type === collectionType)
      .map((tag) => tag.id),
  );
